import type {
  NtBlock, NtCourse, NtPlacement, NtProposal, NtSchedule, NtSelection, NtTerm, NtVisit,
} from '../../core/types'

/**
 * Everyone's NorseTerm checklist, by role and phase — pure, so it can be
 * tested without Firestore (tests/checklist.test.ts).
 *
 * The rule that shapes all of it: **derive, don't ask.** Every item that can
 * be ticked off from the data is — "Plan the three course days" goes green when the
 * planner has no gaps, "Run placement" when placement has run. Nobody has to
 * remember to tick anything, and a checklist cannot claim something is done
 * that is not. The handful of things the app genuinely cannot see (the
 * ranking form lives in Google Forms) are shown as information, with no box.
 */

export type PhaseKey = 'prep' | 'during' | 'show' | 'hist'

export interface CheckItem {
  id: string
  label: string
  /** true / false from the data; null = the app cannot see it, shown without a box. */
  done: boolean | null
  /** A count or a date — "3 waiting", "Due Jan 16". */
  detail?: string
  /**
   * Where you do it — as close to the action as the screen allows: the tab,
   * and where it helps, which course to open, which day, what to scroll to.
   */
  to: { phase: string; tab?: string; params?: Record<string, string> }
  /**
   * Which of an admin's two Prep jobs a step belongs to: approving what
   * teachers and students send in (unset), or building the schedule from it.
   * Named by the job, not the season: courses can be due in December.
   */
  stage?: 'schedule'
}

/** The page a step opens: /norseterm/<page>, keeping any course or day. */
export function stepHref(i: Pick<CheckItem, 'to'>): string {
  if (!i.to.tab) return '/norseterm'
  const q = new URLSearchParams(i.to.params ?? {}).toString()
  return `/norseterm/${i.to.tab}${q ? `?${q}` : ''}`
}

export interface CheckGroup { role: string; items: CheckItem[] }

export const PHASE_LABEL: Record<PhaseKey, string> = {
  prep: 'Norse Term Prep', during: 'During Norse Term', show: 'Open House', hist: 'History',
}

const DAY = ['Tuesday', 'Wednesday', 'Thursday', 'Friday']

/**
 * Which phase it is: Prep until the Tuesday, During Tuesday to Thursday, Open
 * House on the Friday, History after. Prep when no start date is set — the
 * term is not scheduled yet, so getting ready is all there is.
 */
export function currentPhase(term: Pick<NtTerm, 'startDate'>, now: Date = new Date()): PhaseKey {
  if (!term.startDate) return 'prep'
  const start = new Date(term.startDate + 'T00:00:00')
  if (isNaN(start.getTime())) return 'prep'
  const day = Math.floor((startOfDay(now).getTime() - start.getTime()) / 86_400_000)
  if (day < 0) return 'prep'
  if (day <= 2) return 'during'
  if (day === 3) return 'show'
  return 'hist'
}

const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate())

export interface ChecklistInput {
  term: NtTerm
  roles: { teach: boolean; review: boolean; configure: boolean; run: boolean; attend?: boolean; seminar: boolean; student: boolean }
  /** Courses this person teaches. */
  myCourses: NtCourse[]
  /** Every course — what the coordinator schedules. Empty for those who cannot see it. */
  courses: NtCourse[]
  /** How many course rosters this person has, and which days attendance is in for all of them. */
  myRosterCount: number
  myAttendanceDays: Set<number>
  /**
   * Each course this teacher has a roster for, in the order of the day, and
   * whether anyone makes their artifact in it (only they are there Friday).
   * With it, attendance is a step per day and block ("Tuesday morning");
   * without it, one per day.
   */
  mySessions?: Array<{ courseId: string; block: NtBlock | null; title: string; friday: boolean }>
  /** Attendance in, as `${courseId}_${day}`. */
  myAttendanceTaken?: Set<string>
  myVisits: NtVisit[]
  visits: NtVisit[]
  /** Students imported for the term, or null if this person cannot see them. */
  studentsImported: number | null
  placement: NtPlacement | null
  /** Every proposal — for admin and office. */
  proposals: NtProposal[]
  /** Proposals routed to this person as seminar teacher. */
  seminarProposals: NtProposal[]
  awards: number
  /** How many project and course exemplars exist — the admin's to add. */
  exemplars: number
  courseExemplars?: number
  /** A student's own week: undefined = not placed yet. */
  mySchedule?: NtSchedule
  /** For the office: the days (0–3) on which every running course has taken attendance. */
  schoolAttendanceDays?: Set<number>
  /** Everyone's schedules — the office's artifact count. */
  schedules?: NtSchedule[]
  /** A student's own course selections: undefined = none yet. */
  mySelection?: NtSelection
  /** A student's grade, from the class year in their email. */
  myGrade?: number
  /** A student's own proposal: undefined = not started. */
  myProposal?: NtProposal
  /** Whether this person has opened About Norse Term (kept on their device). */
  infoRead?: boolean
  /** Parts of a course plan still unscheduled. Passed in so this stays free of the planner. */
  unplannedParts: (c: NtCourse) => number
}

export function buildChecklist(x: ChecklistInput, phase: PhaseKey): CheckGroup[] {
  const groups: CheckGroup[] = []
  const add = (role: string, items: Array<CheckItem | false | null | undefined>) => {
    const real = items.filter((i): i is CheckItem => !!i)
    if (real.length) groups.push({ role, items: real })
  }
  const due = (iso: string | undefined) => (iso ? `Due ${fmt(iso)}` : undefined)
  const { roles } = x
  const scheduling = (items: Array<CheckItem | false | null | undefined>) =>
    items.map((i) => i && { ...i, stage: 'schedule' as const })

  /* ---- student ---- */
  // Everyone makes course selections, even students proposing a project: a
  // proposal may not be approved, and freshmen take on-site courses only.
  // Tenth through twelfth graders may also propose an independent project,
  // on its own page.
  if (roles.student) {
    // Morning and afternoon, ranksPerBlock each (older terms: half the total).
    const required = 2 * (x.term.rules.ranksPerBlock ?? Math.max(1, Math.ceil((x.term.rules.ranksRequired || 6) / 2)))
    const sel = x.mySelection
    const picked = sel?.ranks.length ?? 0
    const submitted = !!sel?.submittedAt && picked >= required
    const selections: CheckItem = {
      id: 'selections', label: 'Make your course selections', done: submitted,
      detail: submitted ? `${picked} courses ranked. You can change them until the deadline`
        : [picked ? `${picked} of ${required} picked` : `Rank your top ${required}`, due(x.term.deadlines.rankings), 'required for everyone']
          .filter(Boolean).join(' · '),
      to: { phase: 'prep', tab: 'selections' },
    }
    const p = x.myProposal
    const mayPropose = (x.myGrade ?? 12) >= 10
    if (phase === 'prep') {
      add('Student', [
        selections,
        mayPropose && { id: 'project', label: 'Independent project proposal (optional)', to: { phase: 'prep', tab: 'project' },
          done: p ? p.status === 'approved' : null,
          detail: !p ? [due(x.term.deadlines.proposals), 'Only if you have a host lined up'].filter(Boolean).join(' · ')
            : p.status === 'returned' ? 'Sent back with notes: revise it and submit again'
            : p.status === 'draft' ? 'Draft, not submitted yet'
            : p.status === 'approved' ? 'Approved' : 'Being checked' },
        artifactItem(x.mySchedule),
      ])
    }
    if (phase === 'during') {
      add('Student', [
        { id: 'go', label: 'Be where your schedule says, every session', done: null, to: { phase: 'during', tab: 'schedule' } },
        artifactItem(x.mySchedule),
      ])
    }
    return groups
  }

  // Admin and placement first: for anyone who holds them, that is the bigger
  // job in Prep, and an admin's list should read as an admin's list.
  /* ---- admin ---- */
  // A review step is done only once its deadline has passed with nothing
  // left waiting. "Nothing waiting" in October just means nothing has been
  // submitted yet, and ticking it then would say the work was done.
  const reviewStep = (waiting: number, deadline: string | undefined, noun: string): Pick<CheckItem, 'done' | 'detail'> => ({
    done: waiting === 0 && pastDue(deadline),
    detail: waiting ? `${waiting} waiting` : pastDue(deadline) ? 'All done'
      : [`No ${noun} waiting yet`, due(deadline)].filter(Boolean).join(' · '),
  })
  const ready = x.proposals.filter((p) => p.status === 'checked').length
  const projectsItem: CheckItem = {
    id: 'projects', label: 'Verify student independent project proposals',
    ...reviewStep(ready, x.term.deadlines.proposals, 'proposals'), to: { phase: 'prep', tab: 'projects' },
  }
  const awardsItem: CheckItem = {
    id: 'awards', label: 'Record the open-house awards', done: x.awards > 0, to: { phase: 'show', tab: 'awards' },
  }
  if (roles.review || roles.configure) {
    if (phase === 'prep') {
      const waiting = x.courses.filter((c) => c.status === 'submitted').length
      // An admin's steps are their three jobs: verify courses, verify
      // project proposals, build the schedule. One-time setup (dates, example
      // courses and projects) is under Manage, not a step.
      add('Admin', [
        roles.review && { id: 'review', label: 'Verify teacher course proposals',
          ...reviewStep(waiting, x.term.deadlines.courses, 'courses'), to: { phase: 'prep', tab: 'review' } },
        roles.review && projectsItem,
      ])
    }
    if (phase === 'show' && roles.configure) add('Admin', [awardsItem])
  }

  /* ---- office: attendance and where students are ---- */
  // Only for someone who is Office and not also running placement; an admin
  // already sees attendance in the alerts and the reports.
  if (roles.attend && !roles.run) {
    if (phase === 'prep') {
      const coursed = (x.schedules ?? []).filter((s) => !s.independent)
      const open = coursed.filter((s) => !s.artifactBlock).length
      add('Office', [
        x.placement
          ? { id: 'artifactsChosen', label: 'Every student has an artifact course (where they are Friday morning)',
              done: coursed.length > 0 && open === 0, detail: open ? `${open} not chosen` : undefined,
              to: { phase: 'prep', tab: 'artifacts' } }
          : { id: 'waitPlacement', label: 'Rosters and schedules appear here once admin runs placement', done: null,
              to: { phase: 'prep', tab: 'artifacts' } },
      ])
    }
    if (phase === 'during' || phase === 'show') {
      add('Office', (phase === 'during' ? [0, 1, 2] : [3]).map((d) => ({
        id: `schoolAtt${d}`, label: `Attendance in from every course: ${DAY[d]}`,
        done: x.schoolAttendanceDays?.has(d) ?? false, to: { phase: 'during', tab: 'attendance' },
      })))
    }
  }

  /* ---- placement: admin ---- */
  if (roles.run) {
    const approved = x.courses.filter((c) => c.status === 'approved')
    const unblocked = approved.filter((c) => !c.block).length
    if (phase === 'prep') {
      // Building the schedule is one job in four parts, done in this order;
      // the step names the next part and opens its page.
      const imported = (x.studentsImported ?? 0) > 0
      const blocked = approved.length > 0 && unblocked === 0
      const next: { tab: string; text: string } | null =
        !blocked ? { tab: 'blocks', text: approved.length ? `Next: put every approved course in a morning or afternoon block (${unblocked} left)` : 'Starts once courses are approved' }
        : !imported ? { tab: 'students', text: `Next: students make their course selections${x.term.deadlines.rankings ? ` (due ${fmt(x.term.deadlines.rankings)})` : ''}` }
        : !x.placement ? { tab: 'build', text: 'Next: run placement' }
        : x.placement.unplacedCount > 0 ? { tab: 'build', text: `Next: place the last ${x.placement.unplacedCount} students` }
        : null
      add('Placement', scheduling([
        { id: 'schedule', label: 'Build the schedule: where courses and students go', done: !next,
          detail: next?.text ?? `${x.placement?.placedCount ?? 0} placements made`, to: { phase: 'prep', tab: next?.tab ?? 'build' } },
        !roles.review && projectsItem,
      ]))
    }
    if (phase === 'during') {
      const live = approved.filter((c) => c.block)
      const unseen = live.filter((c) => !x.visits.some((v) => v.courseId === c.id)).length
      add('Placement', [
        { id: 'visits', label: 'Assign a site visit to every course', done: live.length > 0 && unseen === 0,
          detail: unseen ? `${unseen} not covered` : undefined, to: { phase: 'during', tab: 'visits' } },
      ])
    }
    if (phase === 'show' && !roles.configure) add('Placement', [awardsItem])
  }

  /* ---- teacher ---- */
  if (roles.teach) {
    // Credit recovery sections are admin's to set up: nothing to write or send.
    const own = x.myCourses.filter((c) => !c.assignedOnly)
    const sections = x.myCourses.filter((c) => c.assignedOnly)
    const sectionItem: CheckItem | false = sections.length > 0 && {
      id: 'credit', label: `You teach credit recovery (${sections.map((c) => (c.block === 'AM' ? 'morning' : 'afternoon')).join(' and ')})`,
      done: null, detail: 'Admin sets it up and places students in it. Nothing to write or send.', to: { phase: 'prep', tab: 'mine' },
    }
    if (phase === 'prep' && !own.length && sectionItem) {
      add('Teacher', [
        { id: 'info', label: 'Read about Norse Term', done: !!x.infoRead, to: { phase: 'prep', tab: 'info' } },
        sectionItem,
      ])
    } else if (phase === 'prep') {
      // Sent back means it is not with a reviewer any more: back to the teacher.
      const submitted = own.filter((c) => c.status === 'submitted' || c.status === 'approved')
      const gaps = own.reduce((n, c) => n + x.unplannedParts(c), 0)
      const approved = own.some((c) => c.status === 'approved')
      const returned = own.find((c) => c.status === 'returned')
      // The course each item should open: the one that needs the work.
      const draft = own.find((c) => c.status === 'draft')
      const gappy = own.find((c) => x.unplannedParts(c) > 0)
      const first = returned ?? draft ?? own[0]
      const mine = (c: NtCourse | undefined, extra: Record<string, string> = {}) =>
        ({ phase: 'prep', tab: 'mine', ...(c ? { params: { course: c.id, ...extra } } : {}) })
      // The course overview is done when it has what a reviewer needs to read:
      // a real title, a description, and something students can make.
      const missing = (c: NtCourse) => [
        (!c.title.trim() || c.title === 'Untitled course') && 'a title',
        !c.desc.trim() && 'a description',
        !(c.artifactOptions ?? []).some((o) => o.description.trim()) && 'artifact options',
      ].filter(Boolean) as string[]
      const overviewGap = first ? missing(first) : null
      // In the order the work happens: read, overview, plan, send, approval.
      add('Teacher', [
        { id: 'info', label: 'Read about Norse Term', done: !!x.infoRead,
          detail: x.infoRead ? undefined : 'Two minutes: the week, the dates, what we need from you', to: { phase: 'prep', tab: 'info' } },
        { id: 'start', label: 'Create your course overview', done: !!first && overviewGap!.length === 0,
          detail: !first ? due(x.term.deadlines.courses) : overviewGap!.length ? `Still needs ${overviewGap!.join(', ')}` : undefined,
          to: mine(first) },
        { id: 'plan', label: 'Plan the three course days', done: own.length > 0 && gaps === 0,
          // An approved course is locked — saying "plan it" there would send
          // them to a planner they cannot change.
          detail: !own.length || !gaps ? undefined
            : gappy?.status === 'approved' ? `${gaps} parts unplanned. Approved courses are locked; ask your reviewer to send it back`
            : `${gaps} of 6 parts not planned`,
          to: mine(gappy ?? first, { focus: 'plan' }) },
        { id: 'propose', label: 'Send your course proposal to admin for review',
          done: submitted.length > 0,
          detail: submitted.length ? undefined : returned ? 'Sent back: revise it and send it again' : due(x.term.deadlines.courses),
          to: mine(returned ?? draft) },
        { id: 'approved', label: 'Course proposal approved by admin', done: approved,
          detail: returned ? 'Sent back, see the note' : undefined, to: mine(first) },
        sectionItem,
      ])
    }
    // Placement has run: who is coming, months ahead of the week.
    if (phase === 'prep' && x.myRosterCount > 0) {
      add('Teacher', [{ id: 'rosterReady', label: 'Your roster is ready: see who is in your class', done: null,
        detail: 'Practice taking attendance before the week, too', to: { phase: 'prep', tab: 'myroster' } }])
    }
    if (phase === 'during' || phase === 'show') {
      add('Teacher', x.myRosterCount === 0
        ? [{ id: 'roster', label: 'Your roster appears once placement runs', done: null, to: { phase: 'during', tab: 'attend' } }]
        : x.mySessions ? sessionSteps(x.mySessions, x.myAttendanceTaken ?? new Set(), phase === 'during' ? [0, 1, 2] : [3])
        : (phase === 'during' ? [0, 1, 2] : [3]).map((d) => ({
            id: `att${d}`, label: `Take attendance: ${DAY[d]}`, done: x.myAttendanceDays.has(d),
            to: { phase: 'during', tab: 'attend', params: { day: String(d) } },
          })))
    }
  }

  /* ---- seminar teacher ---- */
  if (roles.seminar && phase === 'prep') {
    const waiting = x.seminarProposals.filter((p) => p.status === 'submitted').length
    add('Seminar teacher', [
      { id: 'seminar', label: 'Check your seminar students’ independent project proposals',
        ...reviewStep(waiting, x.term.deadlines.proposals, 'proposals'), to: { phase: 'prep', tab: 'seminar' } },
    ])
  }

  /* ---- anyone sent on site visits ---- */
  if ((phase === 'during' || phase === 'show') && x.myVisits.length) {
    const logged = x.myVisits.filter((v) => !!v.loggedAt).length
    add('Site visits', [
      { id: 'myvisits', label: 'Do your site visits and log a note', done: logged === x.myVisits.length,
        detail: `${logged} of ${x.myVisits.length} logged`, to: { phase: 'during', tab: 'visits' } },
    ])
  }

  return groups
}

/** A student's artifact choice — information until they are placed, then a box. */
function artifactItem(s: NtSchedule | undefined): CheckItem | null {
  const label = 'Pick which course you make your open-house artifact in'
  if (!s) return { id: 'artifact', label, done: null,
    detail: 'Opens once you are placed in a morning and an afternoon course. Your artifact is what you show at the open house on Friday',
    to: { phase: 'prep', tab: 'schedule' } }
  if (s.independent || s.noArtifact) return null
  const title = (b: 'AM' | 'PM') => (b === 'AM' ? s.amTitle : s.pmTitle) ?? 'your course'
  return { id: 'artifact', label, done: !!s.artifactBlock,
    detail: s.artifactBlock ? `${title(s.artifactBlock)} (${s.artifactBlock === 'AM' ? 'morning' : 'afternoon'})`
      : s.amCourseId && s.pmCourseId ? `Your morning course (${title('AM')}) or your afternoon course (${title('PM')})`
      : 'One of your two courses',
    to: { phase: 'prep', tab: 'schedule' } }
}

/** How far along: only items the app can see count. */
export function progress(groups: CheckGroup[]): { done: number; total: number } {
  const items = groups.flatMap((g) => g.items).filter((i) => i.done !== null)
  return { done: items.filter((i) => i.done).length, total: items.length }
}

/** Whether a deadline (an ISO date) has passed: the day after it, local time. */
function pastDue(iso: string | undefined, now: Date = new Date()): boolean {
  if (!iso) return false
  const d = new Date(iso + 'T23:59:59')
  return !isNaN(d.getTime()) && now > d
}

function fmt(iso: string): string {
  const d = new Date(iso + 'T12:00:00')
  return isNaN(d.getTime()) ? iso : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
}

/**
 * One attendance step per day and block a teacher has a class: "Tuesday
 * morning", "Tuesday afternoon". Friday morning is artifact prep, so a course
 * is there only if someone makes their artifact in it.
 */
export function sessionLabel(day: number, block: NtBlock | null) {
  return day === 3 ? 'Friday morning (artifact prep)' : `${DAY[day]} ${block === 'PM' ? 'afternoon' : 'morning'}`
}
export function sessionSteps(
  sessions: Array<{ courseId: string; block: NtBlock | null; title: string; friday: boolean }>,
  taken: Set<string>, days: number[],
): CheckItem[] {
  return days.flatMap((d) => {
    const here = sessions.filter((x) => d !== 3 || x.friday)
    return here.map((x) => ({
      id: `att${d}${x.block ?? ''}${here.length > 1 && d === 3 ? x.courseId : ''}`,
      label: `Take attendance: ${sessionLabel(d, x.block)}${d === 3 && here.length > 1 ? `, ${x.title}` : ''}`,
      done: taken.has(`${x.courseId}_${d}`),
      to: { phase: 'during', tab: 'attend', params: { day: String(d), course: x.courseId } },
    }))
  })
}
